import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import FinancialControlService, {
  FinancialProject,
  RevenueEntry,
} from '../../services/financialControlService';
import { format } from 'date-fns';

const FinancialControlRevenue: React.FC = () => {
  const [projects, setProjects] = useState<FinancialProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [revenues, setRevenues] = useState<RevenueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState<RevenueEntry | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [revenueForm, setRevenueForm] = useState({
    project_id: '',
    description: '',
    amount: '',
    transaction_date: format(new Date(), 'yyyy-MM-dd'),
    category: 'product_sales' as 'product_sales' | 'upsell' | 'affiliate' | 'other',
    payment_method: '',
    platform: '',
    platform_fee_percentage: '',
    platform_fee_amount: '',
    notes: '',
  });

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (projects.length > 0) {
      loadRevenues();
    }
  }, [selectedProject, projects]);

  const loadProjects = async () => {
    try {
      const response = await FinancialControlService.listProjects();
      if (response.success && response.data) {
        setProjects(response.data);
        if (response.data.length > 0 && !revenueForm.project_id) {
          setRevenueForm({ ...revenueForm, project_id: response.data[0].id });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    }
  };

  const loadRevenues = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedProject !== 'all') {
        params.project_id = selectedProject;
      }

      const response = await FinancialControlService.listRevenue(params);
      if (response.success && response.data) {
        setRevenues(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar receitas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (revenue?: RevenueEntry) => {
    if (revenue) {
      setEditingRevenue(revenue);
      setRevenueForm({
        project_id: revenue.project_id,
        description: revenue.description,
        amount: revenue.amount.toString(),
        transaction_date: revenue.transaction_date,
        category: revenue.category,
        payment_method: revenue.payment_method || '',
        platform: revenue.platform || '',
        platform_fee_percentage: revenue.platform_fee_percentage?.toString() || '',
        platform_fee_amount: revenue.platform_fee_amount?.toString() || '',
        notes: revenue.notes || '',
      });
    } else {
      setEditingRevenue(null);
      setRevenueForm({
        project_id: projects[0]?.id || '',
        description: '',
        amount: '',
        transaction_date: format(new Date(), 'yyyy-MM-dd'),
        category: 'product_sales',
        payment_method: '',
        platform: '',
        platform_fee_percentage: '',
        platform_fee_amount: '',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRevenue(null);
  };

  const handleSave = async () => {
    if (!revenueForm.project_id || !revenueForm.description || !revenueForm.amount || !revenueForm.transaction_date) {
      setSnackbar({ open: true, message: 'Preencha todos os campos obrigatórios', severity: 'error' });
      return;
    }

    try {
      const revenueData: any = {
        project_id: revenueForm.project_id,
        description: revenueForm.description,
        amount: parseFloat(revenueForm.amount),
        transaction_date: revenueForm.transaction_date,
        category: revenueForm.category,
        payment_method: revenueForm.payment_method || undefined,
        platform: revenueForm.platform || undefined,
        notes: revenueForm.notes || undefined,
      };

      if (revenueForm.platform_fee_percentage) {
        revenueData.platform_fee_percentage = parseFloat(revenueForm.platform_fee_percentage);
      }
      if (revenueForm.platform_fee_amount) {
        revenueData.platform_fee_amount = parseFloat(revenueForm.platform_fee_amount);
      }

      let response;
      if (editingRevenue) {
        response = await FinancialControlService.updateRevenue(editingRevenue.id, revenueData);
      } else {
        response = await FinancialControlService.createRevenue(revenueData);
      }

      if (response.success) {
        setSnackbar({ 
          open: true, 
          message: editingRevenue ? 'Receita atualizada com sucesso' : 'Receita criada com sucesso', 
          severity: 'success' 
        });
        handleCloseDialog();
        loadRevenues();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro ao salvar receita', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro ao salvar receita', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja deletar esta receita?')) return;

    try {
      const response = await FinancialControlService.deleteRevenue(id);
      if (response.success) {
        setSnackbar({ open: true, message: 'Receita deletada com sucesso', severity: 'success' });
        loadRevenues();
      } else {
        setSnackbar({ open: true, message: response.error || 'Erro ao deletar receita', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Erro ao deletar receita', severity: 'error' });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (date: string) => {
    return format(new Date(date), 'dd/MM/yyyy');
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      product_sales: 'Vendas do Produto',
      upsell: 'Upsell',
      affiliate: 'Comissões de Afiliados',
      other: 'Outras Receitas',
    };
    return labels[category] || category;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Receitas
        </Typography>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Projeto</InputLabel>
            <Select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              label="Projeto"
            >
              <MenuItem value="all">Todos os Projetos</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} color="primary">
            Nova Receita
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRevenues} disabled={loading}>
            Atualizar
          </Button>
        </Stack>
      </Box>

      {/* Tabela */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Plataforma</TableCell>
              <TableCell align="right">Valor Bruto</TableCell>
              <TableCell align="right">Taxa</TableCell>
              <TableCell align="right">Valor Líquido</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {revenues.map((revenue) => (
              <TableRow key={revenue.id} hover>
                <TableCell>{formatDate(revenue.transaction_date)}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {revenue.description}
                  </Typography>
                  {revenue.notes && (
                    <Typography variant="caption" color="text.secondary">
                      {revenue.notes}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={getCategoryLabel(revenue.category)} size="small" />
                </TableCell>
                <TableCell>{revenue.platform || '-'}</TableCell>
                <TableCell align="right">{formatCurrency(revenue.amount)}</TableCell>
                <TableCell align="right">{formatCurrency(revenue.platform_fee_amount)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(revenue.net_amount)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenDialog(revenue)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton size="small" onClick={() => handleDelete(revenue.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {revenues.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary">Nenhuma receita encontrada</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de Criar/Editar Receita */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingRevenue ? 'Editar Receita' : 'Nova Receita'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Projeto</InputLabel>
              <Select
                value={revenueForm.project_id}
                onChange={(e) => setRevenueForm({ ...revenueForm, project_id: e.target.value })}
                label="Projeto"
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Descrição"
              fullWidth
              required
              value={revenueForm.description}
              onChange={(e) => setRevenueForm({ ...revenueForm, description: e.target.value })}
              placeholder="Ex: Venda do Produto X"
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Valor"
                  fullWidth
                  required
                  type="number"
                  value={revenueForm.amount}
                  onChange={(e) => setRevenueForm({ ...revenueForm, amount: e.target.value })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data da Transação"
                  fullWidth
                  required
                  type="date"
                  value={revenueForm.transaction_date}
                  onChange={(e) => setRevenueForm({ ...revenueForm, transaction_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth required>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={revenueForm.category}
                onChange={(e) => setRevenueForm({ ...revenueForm, category: e.target.value as any })}
                label="Categoria"
              >
                <MenuItem value="product_sales">Vendas do Produto</MenuItem>
                <MenuItem value="upsell">Upsell</MenuItem>
                <MenuItem value="affiliate">Comissões de Afiliados</MenuItem>
                <MenuItem value="other">Outras Receitas</MenuItem>
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Plataforma"
                  fullWidth
                  value={revenueForm.platform}
                  onChange={(e) => setRevenueForm({ ...revenueForm, platform: e.target.value })}
                  placeholder="Ex: Kirvano, Hotmart, Kiwify"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Método de Pagamento"
                  fullWidth
                  value={revenueForm.payment_method}
                  onChange={(e) => setRevenueForm({ ...revenueForm, payment_method: e.target.value })}
                  placeholder="Ex: Cartão, PIX"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Taxa da Plataforma (%)"
                  fullWidth
                  type="number"
                  value={revenueForm.platform_fee_percentage}
                  onChange={(e) => {
                    const percentage = parseFloat(e.target.value);
                    const amount = parseFloat(revenueForm.amount);
                    setRevenueForm({
                      ...revenueForm,
                      platform_fee_percentage: e.target.value,
                      platform_fee_amount: percentage && amount ? ((amount * percentage) / 100).toFixed(2) : '',
                    });
                  }}
                  placeholder="Ex: 9.7"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Taxa da Plataforma (R$)"
                  fullWidth
                  type="number"
                  value={revenueForm.platform_fee_amount}
                  onChange={(e) => setRevenueForm({ ...revenueForm, platform_fee_amount: e.target.value })}
                  placeholder="Ex: 97.00"
                />
              </Grid>
            </Grid>

            <TextField
              label="Notas"
              fullWidth
              multiline
              rows={3}
              value={revenueForm.notes}
              onChange={(e) => setRevenueForm({ ...revenueForm, notes: e.target.value })}
              placeholder="Observações adicionais..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingRevenue ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialControlRevenue;

